import axios from 'axios';
import type { AxiosError } from 'axios';
import { useAuthStore } from '../stores/authStore';

/**
 * API Base URL Configuration
 *
 * In production (Vercel): Uses relative URL '/api' since frontend and API
 * are served from the same domain via vercel.json rewrites.
 *
 * In development: Uses absolute URL to localhost:3001 where Express runs,
 * with Vite's proxy forwarding /api requests.
 *
 * Can be overridden via VITE_API_BASE_URL environment variable.
 */
const API_BASE_URL =
  import.meta.env['VITE_API_BASE_URL'] ??
  (import.meta.env.PROD ? '/api' : 'http://localhost:3001/api');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================
// Auth Token Interceptor
// ============================================

/**
 * Request interceptor to add JWT access token to all requests.
 * Automatically includes the Authorization header if user is authenticated.
 *
 * @security Token is retrieved from auth store at request time
 */
apiClient.interceptors.request.use(
  (config) => {
    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor to handle 401 Unauthorized responses.
 * Attempts to refresh the token and retry the request.
 * If refresh fails, logs the user out.
 *
 * @security Automatic token refresh with retry mechanism
 */
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - try to refresh token
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest.url?.includes('/auth/')
    ) {
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const success = await useAuthStore.getState().refreshSession();
          if (success) {
            const newToken = useAuthStore.getState().accessToken;
            if (newToken) {
              onTokenRefreshed(newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return apiClient(originalRequest);
            }
          }
        } catch {
          // Refresh failed - logout happens in refreshSession
        } finally {
          isRefreshing = false;
        }
      } else {
        // Another request is refreshing, wait for it
        return new Promise((resolve) => {
          subscribeTokenRefresh((token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(apiClient(originalRequest));
          });
        });
      }
    }

    // Continue to error logging interceptor
    return Promise.reject(error);
  }
);

// ============================================
// Error Logging Interceptor
// ============================================

/**
 * Flag to prevent infinite loops when logging errors
 * (the error logging endpoint itself might fail)
 */
let isLoggingError = false;

/**
 * Log API error to the backend error logging service.
 * Fire-and-forget approach - we don't want to block the app if logging fails.
 *
 * @param error - Axios error object
 */
async function logApiError(error: AxiosError): Promise<void> {
  // Prevent infinite loops - don't log errors from the error endpoint
  if (isLoggingError || error.config?.url?.includes('/errors')) {
    return;
  }

  isLoggingError = true;

  try {
    const statusCode = error.response?.status ?? 0;
    const severity = statusCode >= 500 ? 'CRITICAL' : 'WARNING';

    // Extract module from URL path (e.g., /api/kpis -> kpis)
    const urlPath = error.config?.url ?? '';
    const module = urlPath.split('/').filter(Boolean)[0] || 'unknown';

    // Build error message
    const message = error.response?.data
      ? typeof error.response.data === 'object' && 'message' in (error.response.data as object)
        ? (error.response.data as { message: string }).message
        : JSON.stringify(error.response.data)
      : error.message;

    await fetch(`${API_BASE_URL}/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        severity,
        type: 'API_ERROR',
        message: `API Error: ${error.config?.method?.toUpperCase()} ${urlPath} - ${message}`,
        stack: error.stack,
        environment: import.meta.env['MODE'] || 'production',
        module,
        action: error.config?.method?.toUpperCase(),
        metadata: {
          url: urlPath,
          method: error.config?.method,
          statusCode,
          responseData: error.response?.data,
          requestData: error.config?.data,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
        },
      }),
    });
  } catch {
    // Silent fail - we don't want logging errors to break the app
    console.error('Failed to log API error');
  } finally {
    isLoggingError = false;
  }
}

/**
 * Response interceptor to log API errors automatically.
 * Only logs 4xx and 5xx errors, excludes the error logging endpoint.
 */
apiClient.interceptors.response.use(
  // Success - pass through
  (response) => response,
  // Error - log and re-throw
  async (error: AxiosError) => {
    // Only log actual HTTP errors (4xx, 5xx)
    if (error.response && error.response.status >= 400) {
      logApiError(error);
    }
    return Promise.reject(error);
  }
);

// ============================================
// KPI Types
// ============================================

/**
 * KPI value with trend information for dashboard display
 */
export interface KPIValue {
  code: string;
  name: string;
  value: number;
  previousValue: number;
  change: number;
  trend: 'up' | 'down';
  target: number;
  targetProgress: number;
  unit: 'currency' | 'percent' | 'number';
}

export interface KPIValuesResponse {
  data: KPIValue[];
  timestamp: string;
  period: string;
}

/**
 * KPI benchmark thresholds
 */
export interface KPIBenchmark {
  poor: number;
  average: number;
  good: number;
  excellent: number;
  industryAverage: number;
  unit: string;
}

/**
 * KPI definition from catalog
 */
export interface KPIDefinition {
  id: string;
  code: string;
  name: string;
  description: string;
  category: string;
  formula: string;
  unit: string | null;
  benchmark: KPIBenchmark | null;
  isSystem: boolean;
}

export interface KPIDefinitionsResponse {
  data: KPIDefinition[];
  total: number;
  categories: string[];
}

/**
 * KPI configuration for organization
 */
export interface KPIConfiguration {
  id: string;
  kpiCode: string;
  kpiName: string;
  category: string;
  targetValue: number | null;
  warningThreshold: number | null;
  criticalThreshold: number | null;
  isEnabled: boolean;
}

export interface KPIConfigurationsResponse {
  data: KPIConfiguration[];
  total: number;
}

/**
 * Fetch current KPI values for dashboard display
 */
export async function fetchKPIValues(period = '30d'): Promise<KPIValuesResponse> {
  const response = await apiClient.get<KPIValuesResponse>('/kpis/current', {
    params: { period },
  });
  return response.data;
}

/**
 * Fetch KPI definitions catalog
 */
export async function fetchKPIDefinitions(category?: string): Promise<KPIDefinitionsResponse> {
  const response = await apiClient.get<KPIDefinitionsResponse>('/kpis/definitions', {
    params: category ? { category } : {},
  });
  return response.data;
}

/**
 * Fetch single KPI definition by code
 */
export async function fetchKPIDefinition(code: string): Promise<{ data: KPIDefinition }> {
  const response = await apiClient.get<{ data: KPIDefinition }>(`/kpis/definitions/${code}`);
  return response.data;
}

/**
 * Fetch KPI configurations for organization
 */
export async function fetchKPIConfigurations(): Promise<KPIConfigurationsResponse> {
  const response = await apiClient.get<KPIConfigurationsResponse>('/kpis/configurations');
  return response.data;
}

/**
 * KPI history data point for sparklines
 */
export interface KPIHistoryPoint {
  date: string;
  value: number;
}

export interface KPIHistoryResponse {
  data: Record<string, KPIHistoryPoint[]>;
  days: number;
  kpis: string[];
}

/**
 * Fetch KPI historical data for sparkline charts
 * @param kpis - Comma-separated list of KPI codes (e.g., 'GMV,AOV,ORDERS')
 * @param days - Number of days of history (default: 7)
 */
export async function fetchKPIHistory(kpis?: string, days = 7): Promise<KPIHistoryResponse> {
  const response = await apiClient.get<KPIHistoryResponse>('/kpis/history', {
    params: { kpis, days },
  });
  return response.data;
}

/**
 * Alert affected metrics with values
 */
export interface AlertMetricValue {
  current: number;
  expected: number;
  change: number | null;
}

export interface AffectedMetrics {
  metrics: string[];
  values?: Record<string, AlertMetricValue>;
  highlights?: Record<string, string | number>;
}

/**
 * Alert API responses
 */
export interface Alert {
  id: string;
  type: string;
  severity: string;
  title: string;
  message: string;
  rootCause: string | null;
  affectedMetrics: AffectedMetrics | null;
  status: string;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt?: string;
  recommendations?: string[];
  relatedAlerts?: {
    id: string;
    title: string;
    severity: string;
    status: string;
    createdAt: string;
  }[];
}

export interface AlertsResponse {
  data: Alert[];
  total: number;
  unreadCount: number;
  criticalCount: number;
  warningCount: number;
  pagination?: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Fetch alerts with optional status filter
 */
export async function fetchAlerts(status = 'ACTIVE'): Promise<AlertsResponse> {
  const response = await apiClient.get<AlertsResponse>('/alerts', {
    params: { status },
  });
  return response.data;
}

/**
 * Revenue data point for charts
 */
interface RevenueDataPoint {
  date: string;
  value: number;
  previousValue: number;
}

interface RevenueResponse {
  data: RevenueDataPoint[];
  period: string;
  granularity: string;
  summary: {
    total: number;
    average: number;
    min: number;
    max: number;
  };
}

/**
 * Fetch revenue time series data
 */
export async function fetchRevenueData(period = '30d'): Promise<RevenueResponse> {
  const response = await apiClient.get<RevenueResponse>('/analytics/revenue', {
    params: { period },
  });
  return response.data;
}

/**
 * Forecast data point
 */
interface ForecastDataPoint {
  date: string;
  predicted: number;
  lowerBound: number;
  upperBound: number;
  confidence: number;
}

interface ForecastResponse {
  data: ForecastDataPoint[];
  horizon: number;
  type?: string;
  model: string | null;
  accuracy: {
    mape: number | null;
    lastUpdated: string;
  };
  message?: string;
}

/**
 * Fetch revenue forecast data
 */
export async function fetchForecast(horizon = 30): Promise<ForecastResponse> {
  const response = await apiClient.get<ForecastResponse>('/analytics/forecast', {
    params: { horizon },
  });
  return response.data;
}

/**
 * Dashboard template
 */
interface DashboardTemplate {
  id: string;
  name: string;
  description: string;
  type: string;
  thumbnail: string;
  widgets: string[];
}

interface DashboardTemplatesResponse {
  data: DashboardTemplate[];
  total: number;
}

/**
 * Fetch available dashboard templates
 */
export async function fetchDashboardTemplates(): Promise<DashboardTemplatesResponse> {
  const response = await apiClient.get<DashboardTemplatesResponse>('/dashboards/templates');
  return response.data;
}

// ============================================
// AI Insights / Recommendations Types
// ============================================

/**
 * Impact details for AI recommendations
 */
export interface InsightImpact {
  revenueImpact?: number;
  estimatedRevenueGain?: number;
  recoverableRevenue?: number;
  projectedRevenue?: number;
  monthlySavings?: number;
  carryingCostSaved?: number;
  marginImprovement?: number;
  productsAffected?: number;
  category?: string;
  riskLevel?: 'LOW' | 'MEDIUM' | 'HIGH';
  [key: string]: unknown;
}

/**
 * Action details for AI recommendations
 */
export interface InsightActionDetails {
  suggestedAction?: string;
  targetProducts?: string[];
  timeline?: string;
  rationale?: string;
  acceptedAt?: string;
  acceptedNotes?: string;
  dismissedAt?: string;
  dismissReason?: string;
  deferredAt?: string;
  deferredUntil?: string;
  deferReason?: string;
  [key: string]: unknown;
}

/**
 * AI Insight / Recommendation
 */
export interface AIInsight {
  id: string;
  type: string;
  title: string;
  description: string;
  impact: InsightImpact | null;
  confidence: number;
  actionType: string;
  actionDetails: InsightActionDetails | null;
  status: 'PENDING' | 'ACCEPTED' | 'DISMISSED' | 'DEFERRED' | 'EXPIRED';
  expiresAt: string | null;
  daysUntilExpiry?: number;
  createdAt: string;
  updatedAt: string;
  relatedInsights?: {
    id: string;
    title: string;
    type: string;
    status: string;
    confidence: number;
    createdAt: string;
  }[];
}

export interface AIInsightsResponse {
  data: AIInsight[];
  total: number;
  pendingCount: number;
  acceptedCount: number;
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface AIInsightsSummary {
  byStatus: {
    pending: number;
    accepted: number;
    dismissed: number;
    deferred: number;
    expired: number;
  };
  byType: Record<string, number>;
  total: number;
  potentialImpact: {
    revenue: number;
    savings: number;
  };
}

/**
 * Fetch AI insights/recommendations with optional filters
 */
export async function fetchAIInsights(status?: string, type?: string): Promise<AIInsightsResponse> {
  const response = await apiClient.get<AIInsightsResponse>('/insights', {
    params: { status, type },
  });
  return response.data;
}

/**
 * Fetch AI insights summary
 */
export async function fetchAIInsightsSummary(): Promise<{ data: AIInsightsSummary }> {
  const response = await apiClient.get<{ data: AIInsightsSummary }>('/insights/summary');
  return response.data;
}

/**
 * Fetch single AI insight by ID
 */
export async function fetchAIInsight(id: string): Promise<{ data: AIInsight }> {
  const response = await apiClient.get<{ data: AIInsight }>(`/insights/${id}`);
  return response.data;
}

/**
 * Accept an AI recommendation
 */
export async function acceptInsight(
  id: string,
  notes?: string
): Promise<{ data: AIInsight; message: string }> {
  const response = await apiClient.post<{ data: AIInsight; message: string }>(
    `/insights/${id}/accept`,
    { notes }
  );
  return response.data;
}

/**
 * Dismiss an AI recommendation
 */
export async function dismissInsight(
  id: string,
  reason?: string
): Promise<{ data: AIInsight; message: string }> {
  const response = await apiClient.post<{ data: AIInsight; message: string }>(
    `/insights/${id}/dismiss`,
    { reason }
  );
  return response.data;
}

/**
 * Defer an AI recommendation
 */
export async function deferInsight(
  id: string,
  deferDays?: number,
  reason?: string
): Promise<{ data: AIInsight; message: string }> {
  const response = await apiClient.post<{ data: AIInsight; message: string }>(
    `/insights/${id}/defer`,
    { deferDays, reason }
  );
  return response.data;
}

// ============================================
// Source Analytics Types & Functions
// ============================================

/**
 * Source summary metrics
 */
export interface SourceMetrics {
  source: string;
  sourceLabel: string;
  orderCount: number;
  revenue: number;
  avgOrderValue: number;
  customerCount: number;
  conversionRate: number;
  revenueShare: number;
  growth: number;
}

export interface SourcesResponse {
  data: SourceMetrics[];
  total: {
    revenue: number;
    orders: number;
    customers: number;
  };
  period: string;
}

/**
 * Source-specific KPI data
 */
export interface SourceKPI {
  code: string;
  name: string;
  value: number;
  previousValue: number;
  change: number;
  unit: string;
}

export interface SourceSummaryResponse {
  data: {
    source: string;
    sourceLabel: string;
    kpis: SourceKPI[];
    trends: { date: string; revenue: number; orders: number }[];
    topProducts: { id: string; name: string; revenue: number; quantity: number }[];
    customerSegments: { segment: string; count: number; revenue: number }[];
  };
  period: string;
}

/**
 * Cross-source comparison data
 */
export interface SourceComparisonData {
  source: string;
  sourceLabel: string;
  metrics: Record<string, number>;
}

export interface SourceComparisonResponse {
  data: SourceComparisonData[];
  metrics: string[];
  period: string;
}

/**
 * Fetch all data sources with metrics
 */
export async function fetchSourcesList(period = '30d'): Promise<SourcesResponse> {
  const response = await apiClient.get<SourcesResponse>('/source-analytics/sources', {
    params: { period },
  });
  return response.data;
}

/**
 * Fetch summary for a specific source
 */
export async function fetchSourceSummary(
  source: string,
  period = '30d'
): Promise<SourceSummaryResponse> {
  const response = await apiClient.get<SourceSummaryResponse>(
    `/source-analytics/${source}/summary`,
    {
      params: { period },
    }
  );
  return response.data;
}

/**
 * Fetch KPIs for a specific source
 */
export async function fetchSourceKPIs(
  source: string,
  period = '30d'
): Promise<{ data: SourceKPI[] }> {
  const response = await apiClient.get<{ data: SourceKPI[] }>(`/source-analytics/${source}/kpis`, {
    params: { period },
  });
  return response.data;
}

/**
 * Fetch trends for a specific source
 */
export async function fetchSourceTrends(
  source: string,
  period = '30d'
): Promise<{ data: { date: string; revenue: number; orders: number }[] }> {
  const response = await apiClient.get<{
    data: { date: string; revenue: number; orders: number }[];
  }>(`/source-analytics/${source}/trends`, { params: { period } });
  return response.data;
}

/**
 * Fetch top products for a specific source
 */
export async function fetchSourceProducts(
  source: string,
  limit = 10
): Promise<{
  data: { id: string; name: string; sku: string; revenue: number; quantity: number }[];
}> {
  const response = await apiClient.get<{
    data: { id: string; name: string; sku: string; revenue: number; quantity: number }[];
  }>(`/source-analytics/${source}/products`, { params: { limit } });
  return response.data;
}

/**
 * Fetch cross-source comparison data
 */
export async function fetchSourceComparison(
  sources: string[],
  metrics: string[] = ['revenue', 'orders', 'aov'],
  period = '30d'
): Promise<SourceComparisonResponse> {
  const response = await apiClient.get<SourceComparisonResponse>('/source-analytics/comparison', {
    params: {
      sources: sources.join(','),
      metrics: metrics.join(','),
      period,
    },
  });
  return response.data;
}

// ============================================
// Source Insights Types & Functions
// ============================================

/**
 * Source-specific insight
 */
export interface SourceInsight {
  id: string;
  source: string;
  sourceLabel: string;
  relatedSources: string[];
  type: string;
  title: string;
  description: string;
  impact: InsightImpact | null;
  confidence: number;
  actionType: string;
  actionDetails: InsightActionDetails | null;
  status: 'PENDING' | 'ACCEPTED' | 'DISMISSED' | 'DEFERRED' | 'EXPIRED';
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SourceInsightsResponse {
  data: SourceInsight[];
  total: number;
  bySource: Record<string, number>;
  pagination: {
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface SourceInsightsSummary {
  bySource: Record<string, number>;
  byStatus: {
    pending: number;
    accepted: number;
    dismissed: number;
    deferred: number;
  };
  total: number;
  crossSourceCount: number;
}

/**
 * Fetch source insights with optional filters
 */
export async function fetchSourceInsights(
  source?: string,
  status?: string,
  type?: string,
  limit = 20,
  offset = 0
): Promise<SourceInsightsResponse> {
  const response = await apiClient.get<SourceInsightsResponse>('/source-insights', {
    params: { source, status, type, limit, offset },
  });
  return response.data;
}

/**
 * Fetch source insights summary
 */
export async function fetchSourceInsightsSummary(): Promise<{ data: SourceInsightsSummary }> {
  const response = await apiClient.get<{ data: SourceInsightsSummary }>('/source-insights/summary');
  return response.data;
}

/**
 * Fetch single source insight
 */
export async function fetchSourceInsight(id: string): Promise<{ data: SourceInsight }> {
  const response = await apiClient.get<{ data: SourceInsight }>(`/source-insights/${id}`);
  return response.data;
}

/**
 * Accept a source insight
 */
export async function acceptSourceInsight(
  id: string,
  notes?: string
): Promise<{ data: SourceInsight; message: string }> {
  const response = await apiClient.post<{ data: SourceInsight; message: string }>(
    `/source-insights/${id}/accept`,
    { notes }
  );
  return response.data;
}

/**
 * Dismiss a source insight
 */
export async function dismissSourceInsight(
  id: string,
  reason?: string
): Promise<{ data: SourceInsight; message: string }> {
  const response = await apiClient.post<{ data: SourceInsight; message: string }>(
    `/source-insights/${id}/dismiss`,
    { reason }
  );
  return response.data;
}

/**
 * Fetch cross-source insights (insights that span multiple sources)
 */
export async function fetchCrossSourceInsights(limit = 10): Promise<{ data: SourceInsight[] }> {
  const response = await apiClient.get<{ data: SourceInsight[] }>('/source-insights/cross-source', {
    params: { limit },
  });
  return response.data;
}

export { apiClient };
